import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAdByIdApi } from "../../api/adsApi";
import styles from "./ChatMessageContent.module.css";

type Props = {
  content: string;
};

type Token = {
  type: "text" | "url";
  value: string;
};

type AdPreview = {
  adId: string;
  url: string;
  title: string;
  image?: string;
};

const URL_REGEX = /(https?:\/\/[^\s]+|\/ads\/[^\s]+)/g;
const MONGO_OBJECT_ID_REGEX = /^[a-f0-9]{24}$/i;

const trimUrl = (value: string): string => {
  let next = value.trim();
  while (/[.,!?;:)\]]$/.test(next)) {
    next = next.slice(0, -1);
  }
  return next;
};

const toAbsoluteUrl = (value: string): string => {
  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return value;
  }
};

const extractAdIdFromUrl = (value: string): string | null => {
  try {
    const parsed = new URL(value, window.location.origin);
    const match = parsed.pathname.match(/^\/ads\/([^/?#]+)/);
    return match?.[1] ? match[1] : null;
  } catch {
    return null;
  }
};

const parseTokens = (raw: string): Token[] => {
  if (!raw) return [{ type: "text", value: "" }];
  const tokens: Token[] = [];
  let lastIndex = 0;
  raw.replace(URL_REGEX, (match, _capture, offset) => {
    if (offset > lastIndex) {
      tokens.push({ type: "text", value: raw.slice(lastIndex, offset) });
    }
    tokens.push({ type: "url", value: trimUrl(match) });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < raw.length) {
    tokens.push({ type: "text", value: raw.slice(lastIndex) });
  }
  return tokens;
};

const getLocalizedTitle = (ad: any): string => {
  const titles = ad?.title_i18n && typeof ad.title_i18n === "object" ? ad.title_i18n : {};
  return (
    (typeof titles.fr === "string" && titles.fr.trim()) ||
    (typeof titles.en === "string" && titles.en.trim()) ||
    (typeof titles.ar === "string" && titles.ar.trim()) ||
    "Annonce"
  );
};

const ChatMessageContent: React.FC<Props> = ({ content }) => {
  const [adPreviews, setAdPreviews] = useState<Record<string, AdPreview>>({});
  const requestedAdIdsRef = useRef<Set<string>>(new Set());
  const tokens = useMemo(() => parseTokens(content), [content]);

  const adLinks = useMemo(() => {
    const entries = tokens
      .filter((token) => token.type === "url")
      .map((token) => {
        const adId = extractAdIdFromUrl(token.value);
        const url = toAbsoluteUrl(token.value);
        return adId ? { adId, url } : null;
      })
      .filter((item): item is { adId: string; url: string } => Boolean(item));
    const dedupById = new Map(entries.map((item) => [item.adId, item]));
    return Array.from(dedupById.values());
  }, [tokens]);

  useEffect(() => {
    let cancelled = false;
    const missing = adLinks.filter((item) => (
      MONGO_OBJECT_ID_REGEX.test(item.adId) &&
      !adPreviews[item.adId] &&
      !requestedAdIdsRef.current.has(item.adId)
    ));
    if (missing.length === 0) return;
    missing.forEach((item) => requestedAdIdsRef.current.add(item.adId));

    const run = async () => {
      const updates: Record<string, AdPreview> = {};
      await Promise.all(
        missing.map(async (item) => {
          try {
            const response = await getAdByIdApi(item.adId);
            const raw: any = response?.data && typeof response.data === "object" ? response.data : {};
            const ad: any = raw?.data && typeof raw.data === "object" ? raw.data : raw;
            const photo = Array.isArray(ad?.photos) && ad.photos[0]
              ? (typeof ad.photos[0] === "string" ? ad.photos[0] : ad.photos[0]?.url || ad.photos[0]?.name)
              : undefined;
            updates[item.adId] = {
              adId: item.adId,
              url: item.url,
              title: getLocalizedTitle(ad),
              image: photo,
            };
          } catch {
            // Ignore preview fetch errors.
          }
        })
      );
      if (cancelled || Object.keys(updates).length === 0) return;
      setAdPreviews((prev) => ({ ...prev, ...updates }));
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [adLinks, adPreviews]);

  return (
    <div className={styles.wrap}>
      <p className={styles.text}>
        {tokens.map((token, index) => {
          if (token.type === "text") {
            return <React.Fragment key={`text-${index}`}>{token.value}</React.Fragment>;
          }
          const href = toAbsoluteUrl(token.value);
          return (
            <a
              key={`url-${index}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              {token.value}
            </a>
          );
        })}
      </p>

      {adLinks.map((item) => {
        const preview = adPreviews[item.adId];
        if (!preview) return null;
        return (
          <a
            key={item.adId}
            href={preview.url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.card}
          >
            {preview.image ? (
              <img src={preview.image} alt={preview.title} className={styles.cardImage} loading="lazy" />
            ) : null}
            <div className={styles.cardBody}>
              <strong className={styles.cardTitle}>{preview.title}</strong>
            </div>
          </a>
        );
      })}
    </div>
  );
};

export default ChatMessageContent;
